import {
  ActivityV1Type,
  ActivityWizardData,
  AssociationItem,
  EditorialPerInitiative,
} from "../../AddActivity/types";
import { isNoObsolescenceDate } from "../../utils/obsolescence";

const SENTINEL_FAR_FUTURE = 253402214400000;

const toIsoOrNull = (d: string | number | null | undefined): string | null => {
  if (d == null || d === "") return null;
  if (typeof d === "number") {
    if (d === SENTINEL_FAR_FUTURE) return null;
    return new Date(d).toISOString();
  }
  if (d === "9999-12-31") return null;
  return d;
};

const toAssociationItem = (
  v: { id: number | string; label?: string; name?: string } | null | undefined,
): AssociationItem | null => {
  if (!v || v.id == null) return null;
  return { id: Number(v.id), label: v.label || v.name || `#${v.id}` };
};

const toAssociationList = (
  list:
    | Array<{ id: number | string; label?: string; name?: string }>
    | null
    | undefined,
): AssociationItem[] => {
  if (!list || !Array.isArray(list)) return [];
  return list
    .map((it) => toAssociationItem(it))
    .filter((x): x is AssociationItem => x !== null);
};

const toLanguageAssociationList = (
  list:
    Array<string | { id: string | number; label?: string }> | null | undefined,
): AssociationItem[] => {
  if (!list || !Array.isArray(list)) return [];
  return list
    .map((it): AssociationItem | null => {
      if (typeof it === "string") return { id: it, label: it };
      if (it && it.id != null)
        return { id: it.id, label: it.label || String(it.id) };
      return null;
    })
    .filter((x): x is AssociationItem => x !== null);
};

const numToString = (n: number | string | null | undefined): string => {
  if (n == null || n === "") return "";
  return String(n);
};

const toBool = (v: any): boolean => {
  if (typeof v === "boolean") return v;
  if (typeof v === "string") return v === "true";
  return !!v;
};

export function mapActivityDetailToWizardData(
  detail: Record<string, any>,
  editorialByInitiative: Record<number, EditorialPerInitiative> = {},
): ActivityWizardData {
  const publishDate = toIsoOrNull(detail.publish_date);
  const obsolescentDate = isNoObsolescenceDate(detail.obsolescent_date)
    ? null
    : toIsoOrNull(detail.obsolescent_date);
  const isStandalone = toBool(detail.is_standalone);
  const detailDescription =
    detail.detail_description || detail.description || "";

  return {
    type: (detail.type || null) as ActivityV1Type | null,

    title: detail.title || "",
    detail_description: detailDescription,
    duration: numToString(detail.duration),
    digited_code: detail.digited_code || "",

    xapi_package_id: toAssociationItem(detail.xapi_package_id),
    scorm_package_id: toAssociationItem(detail.scorm_package_id),
    external_file_url: detail.external_file_url || "",
    is_link_relative: toBool(detail.is_link_relative),
    is_editorial: toBool(detail.is_editorial),
    completion_required: toBool(detail.completion_required),
    flag_authentication: toBool(detail.flag_authentication),
    client_id: detail.client_id || "",
    client_secret: detail.client_secret || "",
    sso_auth: toBool(detail.sso_auth),
    download_flag: toBool(detail.download_flag),
    youtube_video: detail.youtube_video || "",
    external_id: detail.external_id || "",
    lti_account_id: toAssociationItem(detail.lti_account_id),
    survey_id:
      detail.survey_id != null && typeof detail.survey_id === "object"
        ? toAssociationItem(detail.survey_id)
        : detail.survey_id != null
          ? { id: Number(detail.survey_id), label: `#${detail.survey_id}` }
          : null,
    agenda: Array.isArray(detail.agenda) ? detail.agenda : [],
    min_partecipant: numToString(detail.min_partecipant),
    max_partecipant: numToString(detail.max_partecipant),
    min_completition_perc: numToString(detail.min_completition_perc),
    self_enrollment: toBool(detail.self_enrollment),

    cover_id: toAssociationItem(detail.cover_id),
    video_id: toAssociationItem(detail.video_id),

    is_standalone: isStandalone,

    schedule_publication: isStandalone && !!publishDate,
    publish_date: publishDate,
    schedule_obsolescence: isStandalone && !!obsolescentDate,
    obsolescent_date: obsolescentDate,

    fruition_flag: toBool(detail.fruition_flag),

    time_slots: Array.isArray(detail.time_slots) ? detail.time_slots : [],

    didactical_material: toAssociationList(detail.didactical_material),
    useful_links: Array.isArray(detail.useful_links) ? detail.useful_links : [],
    stakeholders: Array.isArray(detail.stakeholders) ? detail.stakeholders : [],

    certificate_ids: toAssociationList(detail.certificate_ids),
    signature_certificate_ids: toAssociationList(
      detail.signature_certificate_ids,
    ),
    badge_id: toAssociationItem(detail.badge_id),

    complexity: detail.complexity || null,
    score: numToString(detail.score),
    credits: numToString(detail.credits),
    lo_tag: Array.isArray(detail.lo_tag) ? detail.lo_tag : [],
    available_languages: toLanguageAssociationList(detail.available_languages),

    selected_initiatives: toAssociationList(detail.initiative),

    editorial_per_initiative: editorialByInitiative,
    highlight_explore: toBool(detail.highlight_explore),
  };
}
